import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { useInvalidateIdentity } from "../api/queries";
import { useTownSession } from "../app/TownSessionContext";
import { signOut } from "../auth/authClient";
import { flagLabel } from "../lib/flag";
import { useTownStore, type QualityLevel } from "../state/store";
import { useReviewerAccess } from "./advertising/useReviewerAccess";
import { useStaffAccess } from "./admin/useStaffAccess";
import { FlagIcon } from "./FlagIcon";

/** Account / guest menu: label + approximate country flag, account links, graphics quality, sign out. */
export function AccountMenu() {
  const session = useTownSession();
  const { actor, user, quality, open } = useTownStore(
    useShallow((s) => ({ actor: s.bootstrap?.actor ?? null, user: s.bootstrap?.session ?? null, quality: s.ui.quality, open: s.ui.menuOpen })),
  );
  const setUi = useTownStore((s) => s.setUi);
  const pushToast = useTownStore((s) => s.pushToast);
  const invalidate = useInvalidateIdentity();
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const reviewer = useReviewerAccess().access === "reviewer";
  // ADR-0035: anyone with a staff permission gets the staff hub (cosmetic; the server checks every staff route).
  const staff = useStaffAccess().access === "staff";

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setUi({ menuOpen: false });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUi({ menuOpen: false });
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setUi]);

  if (!actor) return null;
  const signedIn = Boolean(user?.userId);
  const label = signedIn && user?.displayName ? user.displayName : actor.label;

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="btn btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setUi({ menuOpen: !open })}
        title={signedIn ? "Your account" : "Guest identity (server-issued)"}
      >
        <FlagIcon code={actor.countryCode} />
        <span className="visually-hidden">{flagLabel(actor.countryCode)}, </span>
        <span className="hud-label">{label}</span>
      </button>
      {open ? (
        <div className="menu-popover" role="menu" aria-label="Account menu">
          <div className="menu-heading">
            {signedIn ? (
              <>
                Signed in as <strong>{label}</strong>
                {user?.emailVerified === false ? <div className="small">Email not verified yet</div> : null}
              </>
            ) : (
              <>
                You are <strong>{actor.label}</strong> (guest). Create an account to choose a name that persists.
              </>
            )}
          </div>
          <Link role="menuitem" className="btn btn-ghost btn-sm" to="/account" onClick={() => setUi({ menuOpen: false })}>
            {signedIn ? "Account" : "Sign in or register"}
          </Link>
          {signedIn ? (
            <Link role="menuitem" className="btn btn-ghost btn-sm" to="/orders" onClick={() => setUi({ menuOpen: false })} data-testid="menu-my-orders">
              My orders
            </Link>
          ) : null}
          {signedIn ? (
            <Link role="menuitem" className="btn btn-ghost btn-sm" to="/advertise" onClick={() => setUi({ menuOpen: false })} data-testid="menu-advertise">
              Advertise (signs, billboards, statistics)
            </Link>
          ) : null}
          {reviewer ? (
            <Link role="menuitem" className="btn btn-ghost btn-sm" to="/review" onClick={() => setUi({ menuOpen: false })} data-testid="menu-review">
              Review designs (staff)
            </Link>
          ) : null}
          {staff ? (
            <Link role="menuitem" className="btn btn-ghost btn-sm" to="/admin" onClick={() => setUi({ menuOpen: false })} data-testid="menu-admin">
              Staff tools
            </Link>
          ) : null}
          <Link role="menuitem" className="btn btn-ghost btn-sm" to="/stats" onClick={() => setUi({ menuOpen: false })}>
            Statistics page
          </Link>
          <Link role="menuitem" className="btn btn-ghost btn-sm" to="/directory" onClick={() => setUi({ menuOpen: false })}>
            Directory page (text)
          </Link>
          <Link role="menuitem" className="btn btn-ghost btn-sm" to="/legal" onClick={() => setUi({ menuOpen: false })} data-testid="menu-legal">
            Terms, privacy and payments
          </Link>
          <label className="field" style={{ padding: "6px 8px" }}>
            <span className="small">Graphics quality</span>
            <select
              value={quality}
              onChange={(e) => {
                const q = e.target.value as QualityLevel;
                if (session) session.setQuality(q);
                else useTownStore.getState().setQuality(q);
              }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          {signedIn ? (
            <button
              type="button"
              role="menuitem"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const res = await signOut();
                setBusy(false);
                setUi({ menuOpen: false });
                if (!res.ok) {
                  pushToast(res.message ?? "Sign-out failed.", "warning");
                  return;
                }
                pushToast("Signed out. You are a guest again.", "info");
                await invalidate();
              }}
            >
              {busy ? "Signing out…" : "Sign out"}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
